import { ServiceParameter } from '@/types/store-parameters-types';
import { getFeatureFlagForService } from '@/configs/feature-flags';

/**
 * Single source of truth for ShopiMind sending channels.
 *
 * - `key` matches the backend `OrderAssociationSendingMedia` enum value used to
 *   build the per-shop configuration keys for sales association.
 * - `card` (optional) drives the "Canaux d'envois" cards in the Shop Settings page.
 *   Channels with no `card` are technical media (e.g. on-site display) that have
 *   no user-facing configuration screen.
 *
 * Adding a new channel here makes it appear in:
 *   - the "Canaux d'envois" group on the Shop Settings page (if `card` is set)
 *   - the Sales Association modal (if not gated by a disabled feature flag)
 */

export interface SendingChannelCard {
  /** Identifier expected by `getFeatureFlagForService` and used as the card `name`. */
  serviceName: string;
  titleKey: string;
  subtitleKey: string;
  manageKey?: string;
  component: string;
  icon: string;
  modalWidth: string;
}

export interface SendingChannel {
  /** Stable key — must match backend `OrderAssociationSendingMedia` enum value. */
  key: string;
  /** Icon used in the compact sales-association rows. */
  associationIcon: string;
  /**
   * Whether the channel can attribute a sale on an open *or* a click.
   * When false (default), only clicks are tracked — the action dropdown is hidden
   * and the action is forced to `'click'`.
   */
  supportsOpenClick?: boolean;
  /** Settings-page card metadata. Omit for media that have no user-facing card. */
  card?: SendingChannelCard;
}

export const SENDING_CHANNELS: SendingChannel[] = [
  {
    key: 'email',
    associationIcon: 'fa-thin fa-envelope',
    supportsOpenClick: true,
    card: {
      serviceName: 'email',
      titleKey: 'storeParameters.services.emailServiceParameter.title',
      subtitleKey: 'storeParameters.services.emailServiceParameter.subtitle',
      component: 'EmailServiceParameter',
      icon: 'fal fa-envelope',
      modalWidth: '100rem',
    },
  },
  {
    key: 'sms',
    associationIcon: 'fa-thin fa-message-sms',
    card: {
      serviceName: 'sms',
      titleKey: 'storeParameters.services.smsServiceParameter.title',
      subtitleKey: 'storeParameters.services.smsServiceParameter.subtitle',
      component: 'SmsServiceParameter',
      icon: 'fal fa-message-sms',
      modalWidth: '100rem',
    },
  },
  {
    key: 'pushNotification',
    associationIcon: 'fa-thin fa-bell',
    card: {
      serviceName: 'push_notification',
      titleKey: 'storeParameters.services.pushNotificationServiceParameter.title',
      subtitleKey: 'storeParameters.services.pushNotificationServiceParameter.subtitle',
      component: 'PushNotificationServiceParameter',
      icon: 'fal fa-bell',
      modalWidth: '100rem',
    },
  },
  {
    key: 'whatsapp',
    associationIcon: 'fa-brands fa-whatsapp',
    supportsOpenClick: true,
    card: {
      serviceName: 'whatsapp_business',
      titleKey: 'storeParameters.services.whatsappBusinessParameter.title',
      subtitleKey: 'storeParameters.services.whatsappBusinessParameter.subtitle',
      manageKey: 'storeParameters.services.whatsappBusinessParameter.manage',
      component: 'WhatsappBusinessServiceParameter',
      icon: 'fa-brands fa-whatsapp',
      modalWidth: '64rem',
    },
  },
  {
    key: 'richtexting',
    associationIcon: 'fa-light fa-message-lines',
    supportsOpenClick: true,
    card: {
      serviceName: 'rcs',
      titleKey: 'storeParameters.services.rcsServiceParameter.title',
      subtitleKey: 'storeParameters.services.rcsServiceParameter.subtitle',
      manageKey: 'storeParameters.services.rcsServiceParameter.manage',
      component: 'RcsServiceParameter',
      icon: 'fa-light fa-message-lines',
      modalWidth: '64rem',
    },
  },
  {
    key: 'popup',
    associationIcon: 'fa-thin fa-rectangle-ad',
  },
  {
    key: 'smartContentOnSite',
    associationIcon: 'fa-thin fa-browser',
  },
];

/** Channels rendered on the Shop Settings page (in the "Canaux d'envois" group). */
export const getSendingChannelCards = (): ServiceParameter[] =>
  SENDING_CHANNELS
    .filter((channel): channel is SendingChannel & { card: SendingChannelCard } => Boolean(channel.card))
    .map((channel) => ({
      name: channel.card.serviceName,
      title: channel.card.titleKey,
      subtitle: channel.card.subtitleKey,
      manage: channel.card.manageKey,
      component: channel.card.component,
      icon: channel.card.icon,
      modalWidth: channel.card.modalWidth,
    }));

/**
 * Channels rendered in the Sales Association modal — drops channels gated behind
 * a disabled feature flag (e.g. WhatsApp / RCS while still "Coming soon").
 */
export const getSalesAssociationChannels = (): SendingChannel[] =>
  SENDING_CHANNELS.filter((channel) => {
    const serviceName = channel.card?.serviceName;
    if (!serviceName) return true;
    const flag = getFeatureFlagForService(serviceName);
    return !flag || flag.enabled !== false;
  });
